import { useRef, useEffect } from "react";
import { Box, Typography, Stack, Container } from "@mui/material";
import { useReactToPrint } from "react-to-print";
import numberToWords from "number-to-words";
import { useLocation, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { useSelector } from "react-redux";

const ReceiptPaymentPrint = () => {
  const componentRef = useRef();
  const location = useLocation();
  const navigate = useNavigate();
  const company = useSelector((state) => state.common.companyInfo);
  const companyLogo = company.logo ? JSON.parse(company.logo)?.img : null;
  const { heading, data, tableData } = location.state;

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    onAfterPrint: () => navigate(-1),
  });

  useEffect(() => {
    const timeoutId = setTimeout(() => handlePrint(), 100);
    return () => clearTimeout(timeoutId);
  }, []);

  const totalAmount = tableData.reduce(
    (sum, item) => sum + parseFloat(item.amount || 0),
    0
  );

  const amountInWords = `${numberToWords.toWords(
    Math.floor(totalAmount)
  )} only`;

  return (
    <div ref={componentRef} style={a5Style}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
        <Box style={{ width: "13.3rem", height: "3rem" }}>
          <img
            src={companyLogo}
            alt="Company Logo"
            style={{ width: "100%", height: "100%", objectFit: "contain" }}
          />
        </Box>
        <Container sx={{ textAlign: "right", padding: 0 }}>
          <Typography variant="h5">{company.Name}</Typography>
          <Typography>{company.Add1}</Typography>
          <Typography>{company.city_name}, {company.Place}</Typography>
          <Typography>{company.Country}</Typography>
          {company.Postal && <Typography>Zipcode: {company.Postal}</Typography>}
          {company.Ph && <Typography>Ph: {company.Ph}</Typography>}
          {company.Add2 && <Typography>VAT: {company.Add2}</Typography>}
          <Typography>{company.Web}</Typography>
          <Typography>{company.EMail}</Typography>
        </Container>
      </Stack>

      <Typography align="center" fontSize="14px" fontWeight="bold" mb={1}>
        {heading}
      </Typography>

      <Stack direction="row" justifyContent="space-between" mb={1}>
        <Box>
          <Typography fontSize="10px">
            <strong>Date:</strong> {dayjs(data.date).format("DD-MM-YYYY")}
          </Typography>
          <Typography fontSize="10px"><strong>Ledger:</strong> {data.payTo}</Typography>
          <Typography fontSize="10px"><strong>Transaction Type:</strong> {data.transactionType}</Typography>
          <Typography fontSize="10px"><strong>Typ:</strong> {data.typ}</Typography>
        </Box>
        <Box>
          <Typography fontSize="10px"><strong>Voucher No:</strong> {data.voucherNumber}</Typography>
        </Box>
      </Stack>

      <table style={tableStyle}>
        <thead>
          <tr>
            <th style={thTdStyle}>Description</th>
            <th style={thTdStyle}>Amount</th>
          </tr>
        </thead>
        <tbody>
          {tableData.map((item, idx) => (
            <tr key={idx}>
              <td style={thTdStyle}>{item.description}</td>
              <td style={thTdStyle}>{parseFloat(item.amount).toFixed(2)}</td>
            </tr>
          ))}
          <tr>
            <td style={{ ...thTdStyle, fontWeight: "bold" }}>Total</td>
            <td style={{ ...thTdStyle, fontWeight: "bold" }}>{totalAmount.toFixed(2)}</td>
          </tr>
        </tbody>
      </table>

      <Box mt={1}>
        <Typography fontSize="10px"><strong>Amount in Words:</strong> {amountInWords}</Typography>
        <Typography fontSize="10px"><strong>Old Balance:</strong> {data.oldBalance}</Typography>
        <Typography fontSize="10px"><strong>Balance:</strong> {data.oldBalance - totalAmount}</Typography>
      </Box>

      <Stack direction="row" justifyContent="space-between" mt={3}>
        <Typography fontSize="10px"><strong>Approved By</strong></Typography>
        <Typography fontSize="10px"><strong>{heading === "Receipt" ? "Received By" : "Paid By"}</strong></Typography>
      </Stack>
    </div>
  );
};

const a5Style = {
  width: "21cm",
  height: "14.8cm",
  padding: "1cm",
  fontSize: "10px",
  color: "black",
};

const tableStyle = {
  width: "100%",
  borderCollapse: "collapse",
  fontSize: "10px",
  marginTop: "10px",
};

const thTdStyle = {
  border: "1px solid black",
  padding: "4px",
  textAlign: "left",
};

export default ReceiptPaymentPrint;
